import { useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { v4 } from "uuid";

import type {
  IncidentForm,
  IncidentFormId,
  IncidentWorkItemId,
  IncidentWorkItemWithContext,
  RequestPolicePresenceMinimumFields,
  WorkflowVersion,
} from "@thalamos/vision-common";
import {
  FormSectionSchema,
  IncidentFormIdSchema,
  IncidentFormTypeSchema,
  IncidentWorkItemIdSchema,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { RecordPolicePresenceForm } from "../../forms/RecordPolicePresence/RecordPolicePresenceForm.js";
import {
  RequestPolicePresenceForm,
  RequestPolicePresenceFormSkeleton,
} from "../../forms/RequestPolicePresence/RequestPolicePresenceForm.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { Error as ErrorComponent } from "../../pages/Error/Error.js";
import { createRoute } from "../../routing/createRoute.js";

type RecordPolicePresenceRequestPageInnerProps = {
  incidentWorkItem: IncidentWorkItemWithContext | undefined;
  onSubmit: (
    incidentWorkItemId: IncidentWorkItemId,
    formId: IncidentFormId,
    data: {
      [FormSectionSchema.enum.requestPolicePresenceMain]: Record<
        string,
        unknown
      >;
      [FormSectionSchema.enum.recordPolicePresence]: Record<string, unknown>;
    },
  ) => Promise<void>;
  onCancel: () => void;
};

export function RecordPlaceOfSafetyPageInner({
  incidentWorkItem,
  onCancel,
  onSubmit,
}: RecordPolicePresenceRequestPageInnerProps) {
  const workflowVersion = incidentWorkItem?.incidentWorkItem.workflowVersion as
    WorkflowVersion | undefined;
  const formId = useMemo(() => IncidentFormIdSchema.parse(v4()), []);

  const [requestPolicePresenceFormData, setRequestPolicePresenceFormData] =
    useState<Record<string, unknown> | null>(null);
  const [activeForm, setActiveForm] = useState<
    "requestPolicePresence" | "recordPolicePresence"
  >("requestPolicePresence");
  const toastNotifications = useToastNotifications();

  const monkeyPatchedIncidentWorkItem = useMemo(
    () =>
      incidentWorkItem
        ? {
            ...incidentWorkItem,
            forms: [
              ...incidentWorkItem.forms,

              // Build an in-progress form so it can be picked up by the logic
              // in the form builder to switch between the 'yes' and 'no' form variants
              ...(activeForm === "recordPolicePresence"
                ? [
                    {
                      id: formId,
                      status: "active",
                      incidentWorkItemId: incidentWorkItem.incidentWorkItem.id,
                      sections: [
                        {
                          id: FormSectionSchema.enum.requestPolicePresenceMain,
                          data: requestPolicePresenceFormData ?? {},
                          incidentFormId: formId,
                          updatedAt: new Date(),
                        },
                      ],
                      createdAt: new Date(),
                      updatedAt: new Date(),
                      formType:
                        IncidentFormTypeSchema.enum.requestPolicePresence,
                      formDefinitionVersion:
                        incidentWorkItem.incidentWorkItem.workflowVersion,
                    } as unknown as IncidentForm,
                  ]
                : []),
            ],
          }
        : undefined,
    [
      JSON.stringify(incidentWorkItem),
      JSON.stringify(requestPolicePresenceFormData),
      activeForm,
      formId,
    ],
  );

  return incidentWorkItem === undefined || workflowVersion === undefined ? (
    <RequestPolicePresenceFormSkeleton />
  ) : (
    <>
      <Box
        sx={{
          display: activeForm === "requestPolicePresence" ? "block" : "none",
        }}
      >
        <RequestPolicePresenceForm
          initialValues={undefined}
          onCancel={onCancel}
          onSubmit={(data: RequestPolicePresenceMinimumFields) => {
            setActiveForm("recordPolicePresence");
            setRequestPolicePresenceFormData(data);
            return Promise.resolve();
          }}
          workflowVersion={workflowVersion}
          incidentWorkItem={incidentWorkItem}
        />
      </Box>
      <Box
        sx={{
          display: activeForm === "recordPolicePresence" ? "block" : "none",
        }}
      >
        <RecordPolicePresenceForm
          formId={formId}
          initialValues={undefined}
          workflowVersion={workflowVersion}
          incidentWorkItem={monkeyPatchedIncidentWorkItem}
          onCancel={onCancel}
          onSubmit={async (data) => {
            if (requestPolicePresenceFormData === null) {
              toastNotifications.show(
                "Missing previous form data - please refresh and try again",
                {
                  severity: "error",
                },
              );
              return;
            }
            await onSubmit(incidentWorkItem.incidentWorkItem.id, formId, {
              [FormSectionSchema.enum.requestPolicePresenceMain]:
                requestPolicePresenceFormData,
              [FormSectionSchema.enum.recordPolicePresence]: data,
            });
          }}
        />
      </Box>
    </>
  );
}

export function RecordPolicePresenceRequestPage(
  _props: Record<string, unknown>,
) {
  const params = useParams();
  const navigate = useNavigate();

  const incidentWorkItemId = IncidentWorkItemIdSchema.safeParse(params.id);
  if (!incidentWorkItemId.success) {
    throw new Error("Invalid incident work item id");
  }

  const [
    {
      data: incidentWorkItem,
      loading: isIncidentLoading,
      error: incidentError,
    },
    refetchIncidentWorkItem,
  ] = apiHooks.incidentWorkItem.get(incidentWorkItemId.data);

  if (incidentError) {
    return <ErrorComponent />;
  }

  if (isIncidentLoading) {
    return <CircularProgress />;
  }

  return (
    <RecordPlaceOfSafetyPageInner
      incidentWorkItem={incidentWorkItem ?? undefined}
      onSubmit={async (incidentWorkItemId, formId, data) => {
        await api.incidentWorkItem.recordPolicePresenceRequestAsOfficer(
          incidentWorkItemId,
          formId,
          data,
        );
        await refetchIncidentWorkItem();
        navigate(createRoute.incidentProgressPage(incidentWorkItemId));
      }}
      onCancel={() => {
        navigate(createRoute.incidentProgressPage(incidentWorkItemId.data));
      }}
    />
  );
}
